import React, { useState } from 'react'
import { Card, Form, Input, Button, Typography, Alert, Space } from 'antd'
import { LockOutlined, UserOutlined, CheckCircleOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { customerApi } from '../services/customerApi'
import { useCustomerAuth } from '../contexts/CustomerAuthContext'

const { Title, Text } = Typography

export const PasswordSetup: React.FC = () => {
  const navigate = useNavigate()
  const { customer, business, portalConfig } = useCustomerAuth()
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSetupPassword = async (values: any) => {
    try {
      setLoading(true)
      setError(null)

      await customerApi.setupPassword(values.password)

      setSuccess(true)
      
      // Redirect to configured landing page after success
      const defaultLandingPage = portalConfig?.defaultLandingPage || '/dashboard'
      setTimeout(() => {
        navigate(defaultLandingPage, { replace: true })
      }, 2000)

    } catch (err: any) {
      console.error('Password setup error:', err)
      setError(err.response?.data?.message || 'Failed to set up password. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <Card style={{ width: 400, textAlign: 'center' }}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <CheckCircleOutlined style={{ fontSize: '48px', color: '#52c41a' }} />
            <Title level={3}>Password Set Successfully!</Title>
            <Text type="secondary">
              Your password has been created. You can now log in directly using your email and password.
            </Text>
            <Text type="secondary">Redirecting to your dashboard...</Text>
          </Space>
        </Card>
      </div>
    )
  }

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      <Card style={{ width: 400 }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <Title level={2}>Set Up Your Password</Title>
          <Text type="secondary">
            Welcome to {business?.name}! Create a password to access your customer portal directly.
          </Text>
        </div>

        {error && (
          <Alert
            message="Setup Failed"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: '24px' }}
          />
        )}

        <Form
          name="password-setup"
          onFinish={handleSetupPassword}
          layout="vertical"
          size="large"
        >
          <Form.Item
            label="Email"
            style={{ marginBottom: '16px' }}
          >
            <Input
              prefix={<UserOutlined />}
              value={customer?.email}
              disabled
              style={{ backgroundColor: '#f5f5f5' }}
            />
          </Form.Item>

          <Form.Item
            label="Password"
            name="password"
            rules={[
              { required: true, message: 'Please enter your password' },
              { min: 8, message: 'Password must be at least 8 characters' },
              {
                pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                message: 'Password must contain at least one uppercase letter, one lowercase letter, and one number'
              }
            ]}
            style={{ marginBottom: '16px' }}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Create a strong password"
            />
          </Form.Item>

          <Form.Item
            label="Confirm Password"
            name="confirmPassword"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Please confirm your password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve()
                  }
                  return Promise.reject(new Error('Passwords do not match'))
                }
              })
            ]}
            style={{ marginBottom: '24px' }}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Confirm your password"
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              size="large"
            >
              Create Password
            </Button>
          </Form.Item>
        </Form>

        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            By creating a password, you agree to our terms of service and privacy policy.
          </Text>
        </div>
      </Card>
    </div>
  )
}
